import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import classNames from 'classnames';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import useFeature from '~/store/clinic/hooks/useFeature';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { extractErrorMessage } from '~/utils/error/error';
import { useApiRequest } from '~/hooks/api';
import useClientMultiTeamId from '~/hooks/useClientMultiTeamId';
import { IClinic } from '~/services/api/clinic/types';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import Loader from '~/ui/components/common/Loader';
import { CLIENT, EDIT_CLIENT, MY_CLIENTS, VIEW_CLIENT } from '~/ui/constants/paths';
import EditIcon from '~/ui/assets/images/editWhiteSecond.svg';
import CommunityEngagement from '~/ui/pages/Clients/reusable/CommunityEngagement';
import HealthDetails from '~/ui/pages/Clients/reusable/HealthDetails';
import PersonalDetails from '~/ui/pages/Clients/reusable/PersonalDetails';
import SupportContacts from '~/ui/pages/Clients/reusable/SupportContacts';
import TreatmentPlan from '~/ui/pages/Clients/reusable/TreatmentPlan';

import DocumentsBlock from '~/ui/pages/Clients/reusable/HealthDetails/DocumentsBlock';
import RestoreModal from '../ClientsManagement/RestoreModal/RestoreModal';
import ClientProfileTab, { toClientProfileTab } from './constants/clientProfileTabs';

import styles from './ClientProfile.module.scss';

type ILocationState = {
  pathname: string;
  state: { actTeamId: string; selectedTab: ClientProfileTab };
};

const ClientProfile = (): ReactElement => {
  const navigate = useNavigate();
  const { clientId, actTeamId: teamId } = useParams<{ clientId: string; actTeamId: string }>();
  const location: ILocationState = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [restore, setShowRestore] = useState(false);
  const [requestedTab, setTab] = useState(() =>
    toClientProfileTab(searchParams.get('tab') ?? location?.state?.selectedTab),
  );
  const [loading, setLoading] = useState(true);

  const handleTabChange = (_e: unknown, val: ClientProfileTab) => {
    setTab(val);
    setSearchParams(
      parameter => {
        parameter.set('tab', val);
        return parameter;
      },
      { replace: true },
    );
  };

  const openCommunityEngagement = () => {
    setTab(ClientProfileTab.CommunityEngagement);
    setSearchParams(
      parameter => {
        parameter.set('tab', ClientProfileTab.CommunityEngagement);
        parameter.delete('month');
        return parameter;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (searchParams.get('tab') !== requestedTab) {
      setSearchParams(
        parameter => {
          parameter.set('tab', requestedTab);
          return parameter;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const client = useStoreState(state => state.client.current);

  const { getClientMultiTeamId } = useClientMultiTeamId();
  const clientTeamId = getClientMultiTeamId(client?.actTeams);

  const { isActTeam, team, isGlobalUser } = useRole();
  const {
    isTreatmentPlansAvailable,
    isDocumentsAvailable,
    isBidirectionalAvailable,
    isMedicaidAvailable,
  } = useFeature();

  const actTeamId = location.state?.actTeamId || teamId || team?.id?.toString();

  const clinic = useStoreState(state => state.clinic.current || ({} as IClinic));
  const isExpansionPlan = useStoreState(
    state => !!state.client.medicaidPlan?.medicaidType?.isExpansion,
  );

  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onGetClient, onGetMedicaidPlan, setClient } = useStoreActions(actions => actions.client);
  const { setDefaultClient, resetClientOptions } = useStoreActions(actions => actions.client);
  const { onRestoreTeam } = useStoreActions(actions => actions.clientDetails);

  const onMount = useCallback(async () => {
    try {
      if (!clinic.id) {
        await onGetMyClinic();
      }

      if (clinic.id) {
        await onGetClient({
          clientId,
          teamId: actTeamId,
          clinicId: String(clinic.id),
        });
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [actTeamId, clientId, clinic.id, onGetClient, onGetMyClinic, showError]);

  useEffect(() => {
    onMount();

    return () => {
      setClient(null);
    };
  }, [onMount, setClient]);

  const { isLoading: isPlanLoading } = useApiRequest(
    () => onGetMedicaidPlan({ clinicId: String(clinic.id), teamId: actTeamId, clientId }),
    {
      enabled: !!clinic.id,
      initialLoading: true,
      dependencies: [clinic.id, actTeamId, clientId],
    },
  );

  useEffect(() => {
    if (!client?.isArchived) {
      // Clear options from old team due stale data
      resetClientOptions();
      setDefaultClient(client);
    }
  }, [client, setDefaultClient, resetClientOptions]);

  const isCommunityEngagementPending =
    requestedTab === ClientProfileTab.CommunityEngagement && isPlanLoading;

  if (loading || !client || !clinic || isCommunityEngagementPending) return <Loader />;

  const availableTabs = [
    { value: ClientProfileTab.PersonalDetails, label: 'PERSONAL DETAILS' },
    { value: ClientProfileTab.HealthDetails, label: 'HEALTH DETAILS' },
    ...(isMedicaidAvailable && isExpansionPlan
      ? [{ value: ClientProfileTab.CommunityEngagement, label: 'COMMUNITY ENGAGEMENT' }]
      : []),
    { value: ClientProfileTab.CollateralContacts, label: 'COLLATERAL CONTACTS' },
    ...(isTreatmentPlansAvailable
      ? [{ value: ClientProfileTab.TreatmentPlans, label: 'TREATMENT PLANS' }]
      : []),
    ...(isDocumentsAvailable && isBidirectionalAvailable
      ? [{ value: ClientProfileTab.Documents, label: 'DOCUMENTS' }]
      : []),
  ];
  const tab = availableTabs.some(({ value }) => value === requestedTab)
    ? requestedTab
    : ClientProfileTab.PersonalDetails;

  const itemsForBreadcrumbs = [
    isActTeam || isGlobalUser
      ? { to: MY_CLIENTS, title: `Clients` }
      : { to: CLIENT, title: `${clinic.name} Clients` },
    { to: location.pathname, title: 'Client Profile' },
  ];

  const restoreClient = async (selectedTeamId: string) => {
    try {
      await onRestoreTeam({
        clientId,
        clinicId: String(clinic.id),
        teamId: selectedTeamId,
        id: String(client?.actTeams.find(t => t.team.id === Number(selectedTeamId))?.id || ''),
      });

      await onGetClient({
        clientId,
        teamId: selectedTeamId,
        clinicId: String(clinic.id),
      });

      if (teamId !== selectedTeamId) {
        navigate(
          VIEW_CLIENT.replace(':clientId', String(client?.id)).replace(
            ':actTeamId',
            String(selectedTeamId),
          ),
          {
            state: {
              actTeamId: String(selectedTeamId),
            },
          },
        );
      }
      setShowRestore(false);
      showNotify({ message: 'Client successfully restored' });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const pushToEditClient = () =>
    navigate(
      EDIT_CLIENT.replace(':clientId', clientId).replace(':actTeamId', String(clientTeamId)),
      {
        state: { actTeamId: String(clientTeamId) },
      },
    );

  const startIcon = !client.isArchived && <img src={EditIcon} alt="edit" />;
  const buttonText = client.isArchived ? 'Restore Client' : 'Edit Personal Details';
  const method = client.isArchived ? () => setShowRestore(true) : pushToEditClient;
  const hideButton = tab !== ClientProfileTab.PersonalDetails || isActTeam || isGlobalUser;

  return (
    <div>
      <div className={styles.header}>
        <div>
          <h2>
            {client.firstName} {client.lastName}
          </h2>
          <Breadcrumbs itemsForBreadcrumbs={itemsForBreadcrumbs} />
        </div>
        <div className={styles.headerRightSide}>
          <div className={classNames({ [styles.invisible]: hideButton })}>
            <Button
              color="primary"
              variant="contained"
              startIcon={startIcon}
              onClick={method}
              disabled={hideButton}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
      <Tabs
        value={tab}
        textColor="primary"
        indicatorColor="primary"
        onChange={handleTabChange}
        className={styles.tabsWrapper}
      >
        {availableTabs.map(({ value, label }) => (
          <Tab key={value} value={value} label={label} />
        ))}
      </Tabs>
      {tab === ClientProfileTab.PersonalDetails && (
        <PersonalDetails
          client={client}
          clinicId={String(clinic.id)}
          actTeamId={actTeamId}
          isMedicaidPlanLoading={isPlanLoading}
          onOpenCommunityEngagement={openCommunityEngagement}
        />
      )}
      {tab === ClientProfileTab.HealthDetails && (
        <HealthDetails
          client={client}
          clinicId={String(clinic.id)}
          actTeamId={actTeamId}
          noActions={isActTeam || isGlobalUser}
        />
      )}
      {tab === ClientProfileTab.CommunityEngagement && (
        <CommunityEngagement clinicId={String(clinic.id)} clientId={clientId} />
      )}
      {tab === ClientProfileTab.CollateralContacts && (
        <SupportContacts
          client={client}
          clinicId={String(clinic.id)}
          actTeamId={actTeamId}
          noActions={client.isArchived}
        />
      )}
      {tab === ClientProfileTab.TreatmentPlans && (
        <TreatmentPlan client={client} clinicId={String(clinic.id)} actTeamId={actTeamId} />
      )}
      {tab === ClientProfileTab.Documents && <DocumentsBlock />}
      {restore && (
        <RestoreModal
          client={client}
          onConfirm={restoreClient}
          onClose={() => setShowRestore(false)}
        />
      )}
    </div>
  );
};

export default ClientProfile;
